import React, { useContext } from "react";
import { ProduitInterface } from "dataProvider/Produit";
import { IntlContext } from "context";
import Tooltip from "ui/Form/Element/Tooltip";

export interface InformationsProps {
    produit: ProduitInterface | any;
}
const Informations: React.FC<InformationsProps> = ({ produit }) => {
    interface InfoInterface {
        label?: string;
        value?: string;
        unit?: string;
        tooltip?: string;
    }
    const intl = useContext(IntlContext);
    const boolVal: HashMap<string> = {
        "1": intl.formatMessage("generic.oui"),
        "2": intl.formatMessage("generic.non"),
    };

    // PORTION INFOS
    const portionInfos: InfoInterface[] = [
        {
            label: intl.formatMessage("produit.fiche.label.poids_unite"),
            value: produit?.poids_d_une_unite_g,
            unit: "g",
            tooltip: intl.formatMessage("produit.fiche.tooltip.poids_unite"),
        },
        {
            label: intl.formatMessage("produit.fiche.label.volume_unite"),
            value: produit?.volume_d_une_unite_mL,
            unit: "mL",
            tooltip: intl.formatMessage("produit.fiche.tooltip.volume_unite"),
        },
        {
            label: intl.formatMessage("produit.fiche.label.portion_indiquee"),
            value: produit?.portion_indiquee_g,
            unit: "g",
            tooltip: intl.formatMessage("produit.fiche.tooltip.portion_indiquee"),
        },
        {
            label: intl.formatMessage("produit.fiche.label.portion_indiquee"),
            value: produit?.portion_indiquee_mL,
            unit: "mL",
            tooltip: intl.formatMessage("produit.fiche.tooltip.portion_indiquee"),
        },
        {
            label: intl.formatMessage("produit.fiche.label.portion_vn"),
            value: produit?.portion_VN_g,
            unit: "g",
        },
        {
            label: intl.formatMessage("produit.fiche.label.portion_vn"),
            value: produit?.portion_VN_mL,
            unit: "mL",
        },
    ].filter((p: InfoInterface) => p?.value);

    // AUTRES INFOS
    const autresInfos: InfoInterface[] = [
        {
            label: intl.formatMessage("produit.fiche.label.valeur_indice_carbone"),
            value: produit?.critere_qualenvir,
        },
        {
            label: intl.formatMessage("produit.fiche.label.indice_environnemental"),
            value: produit?.indice_environnemental,
        },
        {
            label: intl.formatMessage("produit.fiche.label.pays_estampille_veterinaire"),
            value: produit?.pays_origine_libel,
        },
        {
            label: intl.formatMessage("produit.fiche.label.outre_mer"),
            value: produit?.outre_mer,
        },
        {
            label: intl.formatMessage("produit.fiche.label.assortiment"),
            value:
                produit?.assortiment && boolVal["" + produit?.assortiment]
                    ? boolVal["" + produit?.assortiment]
                    : undefined,
            tooltip: intl.formatMessage("produit.fiche.tooltip.assortiment"),
        },
        {
            label: intl.formatMessage("produit.fiche.label.conditionnement"),
            value: produit?.conditionnement,
        },
        {
            label: intl.formatMessage("produit.fiche.label.materiau_au_contact"),
            value: produit?.materiau_au_contact,
        },
        {
            label: intl.formatMessage("produit.fiche.label.conseils_preparation"),
            value: produit?.conseil_preparation,
        },
        {
            label: intl.formatMessage("produit.fiche.label.industriel"),
            value: produit?.industriel_libel,
        },
        {
            label: intl.formatMessage("produit.fiche.label.precisions_packaging"),
            value: produit?.packaging,
        },
        {
            label: intl.formatMessage("produit.fiche.label.precisions_produits"),
            value: produit?.libel_precision,
        },
    ].filter((p: InfoInterface) => p?.value);

    const getSourceInfos = (sources: HashMap<any>[]): InfoInterface[] => {
        return sources
            .reduce((memo: string[], source: HashMap<any>) => {
                const excludedValues = ["_"];
                let value: string[] = [];

                const srcObjLibel = source?.source_objet_libel;
                if (srcObjLibel && !excludedValues.includes(srcObjLibel)) {
                    value.push(`${srcObjLibel} : `); // pour autre source
                }

                const srcLibel = source?.source_libel;
                if (srcLibel && !excludedValues.includes(srcLibel)) {
                    value.push(srcLibel); // pour source principale
                }

                if (value.length) {
                    const libel = value.join(" ");
                    // uniq by source objet and source libel
                    if (!memo.includes(libel)) {
                        memo.push(libel);
                    }
                }

                return memo;
            }, [])
            .reduce((memo: InfoInterface[], value: string) => {
                memo.push({ value });
                return memo;
            }, []);
    };

    const sourcePrincipaleInfos: InfoInterface[] = getSourceInfos(produit?.source_principale || []);

    const sourcesInfos: InfoInterface[] = getSourceInfos(produit?.produit_sources || []);

    const renderInfos = (infos: InfoInterface[], label?: string) => {
        return infos.reduce((memo: any, info: any, index: number) => {
            if (info?.value) {
                memo.push(
                    <p key={index}>
                        {label ? `${label} : ` : info?.label ? `${info?.label} : ` : ""}
                        {info?.value} {info?.unit} {info?.tooltip && <Tooltip value={info?.tooltip} />}
                    </p>
                );
            }
            return memo;
        }, []);
    };

    if (!Boolean(portionInfos.length + autresInfos.length + sourcePrincipaleInfos.length + sourcesInfos.length)) {
        return null;
    }

    return (
        <div className="product-informations__block">
            <h2 className="information__title">{intl.formatMessage("produit.fiche.label.autres_informations")}</h2>
            {/* PORTION */}
            {Boolean(portionInfos.length) && (
                <div className="product-others-informations">
                    <p className="font-bold">{intl.formatMessage("produit.fiche.label.portion")}</p>
                    {renderInfos(portionInfos)}
                </div>
            )}
            {/* AUTRES */}
            {Boolean(autresInfos.length) && (
                <div className="product-others-informations">
                    <p className="font-bold">{intl.formatMessage("produit.fiche.label.autres")}</p>
                    {renderInfos(autresInfos)}
                </div>
            )}
            {/* SOURCES */}
            {Boolean(sourcePrincipaleInfos.length || sourcesInfos.length) && (
                <div className="product-others-informations">
                    <p className="font-bold">{intl.formatMessage("produit.fiche.label.sources")}</p>
                    {sourcePrincipaleInfos.length > 0 &&
                        renderInfos(sourcePrincipaleInfos, intl.formatMessage("produit.fiche.label.source_principale"))}
                    {sourcesInfos.length > 0 && renderInfos(sourcesInfos)}
                </div>
            )}
        </div>
    );
};

export default Informations;
